/**
 * services/UserSyncSettingsService.ts
 * Service for managing user sync settings (server-side)
 *
 * Handles universal sync preferences stored in Supabase.
 * One record per user, applies to all devices by default.
 *
 * Architecture: WU1.5 Task 10 - Repository Pattern Migration
 * Service provides orchestration, validation, retry logic, and high-level API
 * Repository handles all database access and snake_case ↔ camelCase mapping
 */

import { userSyncSettingsRepository } from '@/repositories/userSyncSettingsRepository';
import { supabase } from '@/lib/supabase';
import { toDomain } from '@/lib/supabaseMappers';
import { SyncSettingsService } from './SyncSettingsService';
import type {
  UserSyncSettings,
  UserSyncSettingsUpdate
} from '@/types/supabase/UserSyncSettings';
import { DEFAULT_USER_SYNC_SETTINGS } from '@/types/supabase/UserSyncSettings';
import type { SyncProviderType } from '@/types/sync/SyncProvider';
import type { ConflictResolutionStrategy } from '@/types/sync/SyncSettings';
import type { UserSyncConnectionEvent } from '@/types/supabase';

/**
 * Connection status cache interface
 */
interface ConnectionStatus {
  isConnected: boolean;
  email: string | null;
  folderId: string | null;
}

/**
 * Service for managing user sync settings (server-side)
 *
 * Provides high-level API for sync settings with business logic:
 * - Optimistic concurrency with automatic retry
 * - Input validation (sync interval constraints)
 * - Conflict detection and resolution strategy
 *
 * All database access delegated to userSyncSettingsRepository.
 */
export class UserSyncSettingsService {
  /**
   * In-memory cache for cloud connection status
   *
   * CACHED SERVER STATE (UI convenience only - do not trust as source of truth)
   * Refreshed by handleConnectionEvent() when realtime connection events occur.
   * Used to provide cross-tab connection status updates via polling in DataSyncSettings.
   *
   * Pattern: Service owns cache, component polls every 2s to detect changes.
   */
  private cachedConnectionStatus: ConnectionStatus | null = null;

  /**
   * In-flight migration guard (prevents concurrent migration writes)
   *
   * Tracks userId keys for migrations currently in progress.
   * Ensures multiple tabs don't race to update syncOnLogin simultaneously.
   */
  private inFlightMigrations: Set<string> = new Set();

  /**
   * Toast suppression flag for intentional disconnect operations
   *
   * When true, handleConnectionEvent() will return showToast: false for disconnect events.
   * Used during "Start Fresh" cleanup to prevent user-confusing "Sync Disconnected" toast.
   *
   * Pattern: Caller sets true before disconnect, clears in finally block.
   * Fail-safe: Callers must wrap in try/finally to ensure flag is cleared.
   */
  private suppressDisconnectToast: boolean = false;

  /**
   * Flag to suppress "Sync Settings Changed" toasts during intentional operations.
   * Used during "Start Fresh" flow to prevent toast when syncEnabled changes.
   *
   * Pattern: Caller sets true before settings change, clears in finally block.
   * Fail-safe: Callers must wrap in try/finally to ensure flag is cleared.
   */
  private suppressSettingsToast: boolean = false;

  /**
   * Set toast suppression flag for intentional disconnects
   *
   * @param suppress - Whether to suppress disconnect toasts
   */
  setSuppressDisconnectToast(suppress: boolean): void {
    this.suppressDisconnectToast = suppress;
    console.log(`🔕 [Service] suppressDisconnectToast set to: ${suppress}`);
  }

  /**
   * Set toast suppression flag for intentional settings changes
   *
   * @param suppress - Whether to suppress settings change toasts
   */
  setSuppressSettingsToast(suppress: boolean): void {
    this.suppressSettingsToast = suppress;
    console.log(`🔕 [Service] suppressSettingsToast set to: ${suppress}`);
  }

  /**
   * Get user's sync settings with offline fallback
   *
   * Flow (Option A.1 caching strategy):
   * 1. Try to fetch from Supabase (source of truth)
   * 2. If successful: Update IndexedDB cache + return
   * 3. If network error: Return cached IndexedDB value
   * 4. If no cache: Return defaults
   *
   * This provides offline resilience while maintaining Supabase as source of truth.
   *
   * Race-proof: Uses repository upsert pattern to prevent duplicate rows from multiple tabs
   *
   * @param userId - User ID
   * @returns Promise resolving to user sync settings
   */
  async getOrCreateSettings(userId: string): Promise<UserSyncSettings> {
    try {
      // Try server first (source of truth)
      const serverSettings = await userSyncSettingsRepository.getOrCreateByUserId(userId);

      // One-time migration: enable syncOnLogin for users with active provider
      // This migration runs automatically on first settings read after deployment
      if (serverSettings.activeSyncProvider !== null && !serverSettings.syncOnLogin) {
        // Guard: prevent concurrent migrations from multiple tabs
        if (this.inFlightMigrations.has(userId)) {
          console.log(`[Migration] Already migrating for user ${userId.slice(0, 8)}, skipping`);
        } else {
          this.inFlightMigrations.add(userId);

          try {
            const userIdShort = userId.slice(0, 8);

            // Gate: only log once per user to prevent spam on offline retries
            const migrationKey = `migration-syncOnLogin-logged-${userId}`;
            if (!sessionStorage.getItem(migrationKey)) {
              console.log(
                `📋 [Migration] User ${userIdShort} has provider but syncOnLogin=false, migrating to true`
              );
              sessionStorage.setItem(migrationKey, 'true');
            }

            try {
              const updatedSettings = await this.updateSettings(userId, { syncOnLogin: true });
              // Update cache and return migrated settings
              await this.updateCache(updatedSettings);
              return updatedSettings;
            } catch (error) {
              // Only log error once per user per session
              const errorKey = `migration-syncOnLogin-error-${userId}`;
              if (!sessionStorage.getItem(errorKey)) {
                console.error(
                  `❌ [Migration] Failed to migrate syncOnLogin for user ${userIdShort}:`,
                  error
                );
                sessionStorage.setItem(errorKey, 'true');
              }
              // Fall through to return original settings (migration will retry on next call)
            }
          } finally {
            // Always clean up guard to allow future retries on error
            this.inFlightMigrations.delete(userId);
          }
        }
      }

      // Update IndexedDB cache for offline use (write-through cache)
      await this.updateCache(serverSettings);

      return serverSettings;
    } catch (error) {
      console.warn('Failed to fetch settings from server, using cached values', error);

      // Fallback to IndexedDB cache
      const cached = await this.getCachedSettings(userId);
      if (cached) {
        console.log('Using cached settings (offline mode)');
        return cached;
      }

      // No cache available, return defaults (first-time offline user)
      console.warn('No cached settings available, using defaults');
      return this.getDefaultSettings(userId);
    }
  }

  /**
   * Update user sync settings with optional optimistic concurrency check
   *
   * Write-through cache: Updates both Supabase (source of truth) and IndexedDB cache.
   *
   * @param userId - User ID
   * @param updates - Partial settings to update
   * @param expectedVersion - Optional version for optimistic concurrency
   * @returns Promise resolving to updated settings
   * @throws Error if version mismatch (settings modified by another device)
   */
  async updateSettings(
    userId: string,
    updates: UserSyncSettingsUpdate,
    expectedVersion?: number
  ): Promise<UserSyncSettings> {
    // Update server (source of truth)
    const updatedSettings = await userSyncSettingsRepository.update(userId, updates, expectedVersion);

    // Update IndexedDB cache (write-through)
    await this.updateCache(updatedSettings);

    return updatedSettings;
  }

  /**
   * Update settings with automatic retry on version conflict
   *
   * Retry Logic (prevents multi-tab update errors):
   * 1. Fetch current settings to capture version
   * 2. Attempt optimistic concurrency update with version check
   * 3. On conflict, refetch latest and check for field collisions
   * 4. If no collision (different fields changed), retry with fresh version
   * 5. If collision (same field changed on both devices), throw conflict error for UI
   *
   * This keeps multi-tab updates from throwing hard errors in the UI when
   * tabs are modifying different settings simultaneously.
   *
   * @param userId - User ID
   * @param updates - Partial settings to update
   * @returns Promise resolving to updated settings
   * @throws Error with 'CONFLICT:' prefix if fields collide
   */
  async updateSettingsWithRetry(
    userId: string,
    updates: UserSyncSettingsUpdate
  ): Promise<UserSyncSettings> {
    // Get current settings to capture version
    const current = await this.getOrCreateSettings(userId);

    try {
      // Try update with version check
      return await this.updateSettings(userId, updates, current.version);
    } catch (error) {
      if (error instanceof Error && error.message.includes('modified by another device')) {
        // Retry once: fetch latest and check for conflicts
        const fresh = await this.getOrCreateSettings(userId);

        // Check if fields overlap (same field changed on both devices)
        let hasConflict = false;
        for (const key in updates) {
          const updateKey = key as keyof UserSyncSettingsUpdate;
          if (fresh[updateKey] !== current[updateKey]) {
            hasConflict = true;
            break;
          }
        }

        if (hasConflict) {
          // Field collision - let component show modal
          throw new Error('CONFLICT: Settings changed on another device. Please choose which to keep.');
        }

        // No conflict, retry with fresh version
        return await this.updateSettings(userId, updates, fresh.version);
      }
      throw error;
    }
  }

  // ===== CONVENIENCE METHODS (HIGH-LEVEL API) =====
  // These provide semantic operations with validation and retry logic

  /**
   * Update active sync provider
   *
   * @param userId - User ID
   * @param provider - Sync provider type or null
   * @returns Promise resolving to updated settings
   */
  async updateActiveSyncProvider(
    userId: string,
    provider: SyncProviderType | null
  ): Promise<UserSyncSettings> {
    return this.updateSettingsWithRetry(userId, { activeSyncProvider: provider });
  }

  /**
   * Enable or disable sync
   *
   * @param userId - User ID
   * @param enabled - Whether sync should be enabled
   * @returns Promise resolving to updated settings
   */
  async updateSyncEnabled(userId: string, enabled: boolean): Promise<UserSyncSettings> {
    return this.updateSettingsWithRetry(userId, { syncEnabled: enabled });
  }

  /**
   * Update sync interval
   *
   * VALIDATION: Enforces allowed intervals (business rule)
   *
   * @param userId - User ID
   * @param minutes - Sync interval in minutes (must be 1, 5, 10, 15, or 30)
   * @returns Promise resolving to updated settings
   * @throws Error if interval is not in allowed list
   */
  async updateSyncInterval(userId: string, minutes: number): Promise<UserSyncSettings> {
    const allowedIntervals = [1, 5, 10, 15, 30];
    if (!allowedIntervals.includes(minutes)) {
      throw new Error(
        `Invalid sync interval: ${minutes}. Must be one of: ${allowedIntervals.join(', ')}`
      );
    }

    return this.updateSettingsWithRetry(userId, { syncInterval: minutes });
  }

  /**
   * Update conflict resolution strategy
   *
   * @param userId - User ID
   * @param strategy - Conflict resolution strategy
   * @returns Promise resolving to updated settings
   */
  async updateConflictResolution(
    userId: string,
    strategy: ConflictResolutionStrategy
  ): Promise<UserSyncSettings> {
    return this.updateSettingsWithRetry(userId, { conflictResolution: strategy });
  }

  /**
   * Update sync on session close preference
   *
   * @param userId - User ID
   * @param enabled - Whether to sync when closing app/tab
   * @returns Promise resolving to updated settings
   */
  async updateSyncOnSessionClose(userId: string, enabled: boolean): Promise<UserSyncSettings> {
    return this.updateSettingsWithRetry(userId, { syncOnSessionClose: enabled });
  }

  /**
   * Update sync on login preference
   *
   * @param userId - User ID
   * @param enabled - Whether to sync when logging in
   * @returns Promise resolving to updated settings
   */
  async updateSyncOnLogin(userId: string, enabled: boolean): Promise<UserSyncSettings> {
    return this.updateSettingsWithRetry(userId, { syncOnLogin: enabled });
  }

  // ===== GOOGLE DRIVE CONNECTION CHECK =====
  // Wraps Edge Function call to keep Supabase imports out of components

  /**
   * Check Google Drive connection and retrieve account info
   *
   * Calls BFF Edge Function to check OAuth token validity and retrieve account details.
   * This method wraps the Edge Function call to keep Supabase imports out of components.
   *
   * Pattern: Services MAY call supabase.functions.invoke() per CODING_STANDARDS.md
   * Components call this service method instead of calling Edge Function directly.
   *
   * @returns Promise resolving to connection info or null if not connected
   */
  async checkGoogleDriveConnection(): Promise<{
    connected: boolean;
    email: string | null;
    userId: string | null;
    folderId: string | null;
  } | null> {
    try {
      const { data, error } = await supabase.functions.invoke('google-drive-proxy', {
        body: { operation: 'get-connection' }
      });

      if (error) {
        console.error('Failed to check Google Drive connection:', error);
        return null;
      }

      if (!data?.ok) {
        return null;
      }

      return {
        connected: data.connected,
        email: data.email || null,
        userId: data.userId || null,
        folderId: data.folderId || null
      };
    } catch (error) {
      console.error('Failed to check Google Drive connection:', error);
      return null;
    }
  }

  // ===== CONNECTION EVENT HANDLER =====
  // NEW FOR TASK 2.9: Handle connection events from realtime subscription

  /**
   * Handle connection event from realtime subscription
   *
   * Called when oauth_cloud_tokens.disconnected_at changes trigger fires INSERT
   * Pattern: Component passes event, service processes and returns action
   *
   * NEW (Task 2.8 Phase 1B): Also refreshes connection status cache after event
   * so DataSyncSettings polling can detect changes and update GoogleDriveProviderCard UI
   *
   * @param event - Connection event from realtime
   * @returns Promise resolving to object indicating whether to show toast + updated connection state
   */
  async handleConnectionEvent(event: import('@/types/supabase').UserSyncConnectionEvent): Promise<{
    showToast: boolean;
    toastMessage?: string;
    isConnected: boolean;
  }> {
    console.log('🔄 [Service] handleConnectionEvent() called with:', event);
    console.log('🔎 [Service] event.provider =', event.provider, '(type:', typeof event.provider + ')');

    // CAPTURE suppression flag BEFORE any async work
    // This prevents race condition where cleanup clears the flag during BFF await
    const shouldSuppressDisconnectToast = this.suppressDisconnectToast;
    if (shouldSuppressDisconnectToast) {
      console.log('🔕 [Service] Captured suppressDisconnectToast=true at start of handler');
    }

    // Map provider slug to user-facing label for toast
    const providerLabel = event.provider === 'google-drive' ? 'Google Drive' : event.provider;

    // Refresh connection status from server after event
    // This updates cache so DataSyncSettings polling can detect changes
    if (event.provider === 'google-drive') {
      console.log('🌐 [Service] provider is google-drive, refreshing connection status via BFF...');
      const connectionInfo = await this.checkGoogleDriveConnection();
      console.log('✅ [Service] checkGoogleDriveConnection() returned:', connectionInfo);
      if (connectionInfo) {
        this.updateCachedConnectionStatus({
          isConnected: connectionInfo.connected,
          email: connectionInfo.email,
          folderId: connectionInfo.folderId
        });
      } else {
        console.warn('⚠️ [Service] No connection info returned from BFF');
      }
    } else {
      console.warn('⚠️ [Service] Provider is NOT google-drive, skipping cache refresh. Provider:', event.provider);
    }

    // Prepare return payload
    // Use captured flag value (from before async work) for disconnect suppression
    if (event.eventType === 'disconnected' && shouldSuppressDisconnectToast) {
      console.log('🔕 [Service] Suppressing disconnect toast (intentional disconnect)');
    }

    const returnPayload = event.eventType === 'disconnected'
      ? {
          showToast: !shouldSuppressDisconnectToast,
          toastMessage: `${providerLabel} sync disconnected. Reconnect to resume automatic backup.`,
          isConnected: false
        }
      : {
          showToast: true,
          toastMessage: `${providerLabel} sync reconnected. Automatic backup resumed.`,
          isConnected: true
        };

    console.log('📤 [Service] handleConnectionEvent() returning:', returnPayload);
    return returnPayload;
  }

  // ===== SETTINGS UPDATE HANDLER =====
  // NEW FOR TASK 2.10: Handle settings updates from realtime subscription

  /**
   * Handle settings update from realtime subscription
   *
   * Compares previous vs current settings and returns smart toast decisions.
   * Only toasts for behaviorally significant changes (not cosmetic).
   *
   * Pattern: AppWrapper passes previous + current, service diffs and returns action.
   * This keeps AppWrapper dumb - service owns policy about which changes matter.
   *
   * @param previousSettings - Settings before the update (or null if first load)
   * @param currentSettings - Updated settings from realtime event
   * @returns Object indicating whether to show toast + message + what changed + BackgroundSyncService impact
   */
  handleSettingsUpdate(
    previousSettings: UserSyncSettings | null,
    currentSettings: UserSyncSettings
  ): {
    showToast: boolean;
    toastMessage?: string;
    changedField?: 'syncEnabled' | 'syncInterval' | 'syncOnSessionClose' | 'syncOnLogin' | 'conflictResolution' | 'activeSyncProvider';
    previousValue?: unknown;
    currentValue?: unknown;
    shouldAffectBackgroundSync: boolean;
  } {
    // Silent initialization (first load, no previous settings to compare)
    if (!previousSettings) {
      return {
        showToast: false,
        shouldAffectBackgroundSync: false
      };
    }

    // Check suppression flag (used during "Start Fresh" to prevent toast)
    const shouldSuppressToast = this.suppressSettingsToast;
    if (shouldSuppressToast) {
      console.log('🔕 [Service] Suppressing settings toast (intentional change)');
    }

    // Check for behavioral changes (prioritize most impactful first)

    // 1. Sync enabled/disabled (highest priority - affects all sync behavior)
    if (previousSettings.syncEnabled !== currentSettings.syncEnabled) {
      return {
        showToast: !shouldSuppressToast,
        toastMessage: currentSettings.syncEnabled
          ? 'Automatic sync enabled'
          : 'Automatic sync disabled',
        changedField: 'syncEnabled',
        previousValue: previousSettings.syncEnabled,
        currentValue: currentSettings.syncEnabled,
        shouldAffectBackgroundSync: true // BackgroundSyncService needs to start/stop
      };
    }

    // 2. Sync interval changed (high priority - affects background job cadence)
    if (previousSettings.syncInterval !== currentSettings.syncInterval) {
      return {
        showToast: !shouldSuppressToast,
        toastMessage: `Sync interval changed to ${currentSettings.syncInterval} minute${currentSettings.syncInterval === 1 ? '' : 's'}`,
        changedField: 'syncInterval',
        previousValue: previousSettings.syncInterval,
        currentValue: currentSettings.syncInterval,
        shouldAffectBackgroundSync: true // BackgroundSyncService needs to adjust timer
      };
    }

    // 3. Sync on session close (medium priority - affects logout behavior)
    if (previousSettings.syncOnSessionClose !== currentSettings.syncOnSessionClose) {
      return {
        showToast: !shouldSuppressToast,
        toastMessage: currentSettings.syncOnSessionClose
          ? 'Sync on session close enabled'
          : 'Sync on session close disabled',
        changedField: 'syncOnSessionClose',
        previousValue: previousSettings.syncOnSessionClose,
        currentValue: currentSettings.syncOnSessionClose,
        shouldAffectBackgroundSync: false // May change later if we wire logout-time sync
      };
    }

    // 4. Sync on login (medium priority - affects login behavior)
    if (previousSettings.syncOnLogin !== currentSettings.syncOnLogin) {
      return {
        showToast: !shouldSuppressToast,
        toastMessage: currentSettings.syncOnLogin
          ? 'Sync on login enabled'
          : 'Sync on login disabled',
        changedField: 'syncOnLogin',
        previousValue: previousSettings.syncOnLogin,
        currentValue: currentSettings.syncOnLogin,
        shouldAffectBackgroundSync: false
      };
    }

    // 5. Active sync provider changed (medium priority - affects where data syncs)
    if (previousSettings.activeSyncProvider !== currentSettings.activeSyncProvider) {
      let message: string;
      if (!currentSettings.activeSyncProvider) {
        message = 'Sync provider disconnected';
      } else if (currentSettings.activeSyncProvider === 'google-drive') {
        message = 'Sync provider changed to Google Drive';
      } else if (currentSettings.activeSyncProvider === 'local') {
        message = 'Sync provider changed to Local Directory';
      } else {
        message = `Sync provider changed to ${currentSettings.activeSyncProvider}`;
      }

      return {
        showToast: !shouldSuppressToast,
        toastMessage: message,
        changedField: 'activeSyncProvider',
        previousValue: previousSettings.activeSyncProvider,
        currentValue: currentSettings.activeSyncProvider,
        shouldAffectBackgroundSync: false // Provider changes handled separately via connection events
      };
    }

    // 6. Conflict resolution strategy (low priority - silent, not user-facing enough)
    // Intentionally no toast for conflictResolution changes

    // No behavioral changes detected
    return {
      showToast: false,
      shouldAffectBackgroundSync: false
    };
  }

  // ===== REALTIME SUBSCRIPTION METHODS =====
  // NEW FOR TASK 2.9: Service-managed realtime subscriptions (Option 2 architecture)

  /**
   * Subscribe to user sync settings changes via realtime
   *
   * Service-managed subscription pattern:
   * - Service creates/tears down the Supabase channel
   * - Service normalizes wire data → domain data
   * - Service updates cache automatically
   * - Component just receives domain objects via callback
   *
   * @param userId - User ID to filter subscription
   * @param onUpdate - Callback invoked when settings update (receives domain object)
   * @returns Unsubscribe function to clean up channel
   */
  subscribeToSettingsChanges(
    userId: string,
    onUpdate: (settings: UserSyncSettings) => void
  ): () => void {
    const channel = supabase
      .channel(`user-sync-settings-${userId}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'user_sync_settings',
        filter: `user_id=eq.${userId}`
      }, async (payload) => {
        console.log('🔄 Realtime: Settings updated on another device/tab', payload);

        try {
          // Convert wire → domain and update cache
          const updated = await this.applyRealtimeUpdate(payload.new);

          // Notify component
          onUpdate(updated);
        } catch (error) {
          console.error('Failed to apply realtime settings update:', error);
        }
      })
      .subscribe();

    // Return cleanup function
    return () => {
      supabase.removeChannel(channel);
    };
  }

  /**
   * Subscribe to connection events via realtime
   *
   * Service-managed subscription pattern:
   * - Service creates/tears down the Supabase channel
   * - Service normalizes wire data → domain data
   * - Service applies business logic (toast decisions)
   * - Component receives structured result via callback
   *
   * @param userId - User ID to filter subscription
   * @param onConnectionEvent - Callback invoked when connection status changes
   * @returns Unsubscribe function to clean up channel
   */
  subscribeToConnectionEvents(
    userId: string,
    onConnectionEvent: (result: {
      showToast: boolean;
      toastMessage?: string;
      isConnected: boolean;
    }) => void
  ): () => void {
    const channel = supabase
      .channel(`connection-events-${userId}`)
      .on('postgres_changes', {
        event: 'INSERT',  // Trigger fires INSERT on connect/disconnect
        schema: 'public',
        table: 'user_sync_connection_events',
        filter: `user_id=eq.${userId}`
      }, async (payload) => {
        console.log('🔄 Realtime: Connection event', payload);

        try {
          // Convert wire → domain
          const event = toDomain<UserSyncConnectionEvent>(payload.new);
          console.log('🔄 [Subscribe] Converted domain event:', event);
          console.log('🔄 [Subscribe] About to call handleConnectionEvent...');

          // Apply business logic (now async - refreshes connection cache)
          const result = await this.handleConnectionEvent(event);
          console.log('🔄 [Subscribe] handleConnectionEvent returned:', result);

          // Notify component
          onConnectionEvent(result);
        } catch (error) {
          console.error('❌ [Subscribe] Failed to handle connection event:', error);
        }
      })
      .subscribe();

    // Return cleanup function
    return () => {
      supabase.removeChannel(channel);
    };
  }

  /**
   * Apply realtime update from Supabase subscription (PRIVATE HELPER)
   *
   * Normalizes wire format payload to domain type and updates cache.
   * Called internally by subscribeToSettingsChanges().
   *
   * IMPORTANT: This method MUST NOT call the remote API.
   * It only normalizes the payload and updates in-memory + IndexedDB caches.
   * No network round trip to prevent infinite loops or double-fetch.
   *
   * @param wireData - Raw payload.new from Supabase postgres_changes event
   * @returns Domain-typed UserSyncSettings
   */
  private async applyRealtimeUpdate(wireData: unknown): Promise<UserSyncSettings> {
    // Convert wire (snake_case) to domain (camelCase)
    const updated = toDomain<UserSyncSettings>(wireData);

    // Write-through cache (keep IndexedDB in sync)
    // NO API CALL - just cache update to prevent infinite loops
    await this.updateCache(updated);

    return updated;
  }

  // ===== CACHE MANAGEMENT =====
  // Option A.1: IndexedDB as offline cache + device-specific state
  // Made public for Task 2.7 realtime helper

  /**
   * Update IndexedDB cache with server settings
   *
   * Write-through cache pattern: After every successful server read/write,
   * update the local IndexedDB cache for offline access.
   *
   * Made public for Task 2.7: Realtime subscription helper needs access.
   *
   * Cache format (camelCase domain fields):
   * - Device-specific: directoryId (preserved from existing local record)
   * - Cached server state: all universal preferences
   * - Cache metadata: lastServerSync timestamp
   *
   * @param serverSettings - Settings from Supabase (source of truth)
   */
  async updateCache(serverSettings: UserSyncSettings): Promise<void> {
    try {
      const syncSettingsService = new SyncSettingsService();
      const local = await syncSettingsService.getOrCreateDefault();

      // Update cache with server state, preserving device-specific directoryId
      await syncSettingsService.update(local.id, {
        // Cached server state (universal preferences)
        activeSyncProvider: serverSettings.activeSyncProvider,
        syncEnabled: serverSettings.syncEnabled,
        syncInterval: serverSettings.syncInterval,
        syncOnSessionClose: serverSettings.syncOnSessionClose,
        syncOnLogin: serverSettings.syncOnLogin,
        conflictResolution: serverSettings.conflictResolution,

        // Cache metadata
        lastServerSync: new Date().toISOString(),

        // Device-specific state (preserve existing value)
        directoryId: local.directoryId,
      });
    } catch (error) {
      // Cache update failure should not break the main flow
      console.warn('Failed to update IndexedDB cache, continuing with server settings', error);
    }
  }

  /**
   * Get cached settings from IndexedDB
   *
   * Fallback for offline mode: Return last-known-good settings from cache.
   *
   * @param userId - User ID (for constructing UserSyncSettings shape)
   * @returns Cached settings or null if never synced
   */
  private async getCachedSettings(userId: string): Promise<UserSyncSettings | null> {
    try {
      const syncSettingsService = new SyncSettingsService();
      const local = await syncSettingsService.getOrCreateDefault();

      // Check if cache has ever been populated from server
      if (!local.lastServerSync) {
        return null; // Never synced, no trustworthy cache
      }

      // Convert IndexedDB cache to UserSyncSettings shape
      // IMPORTANT: Cache stores camelCase domain fields (not snake_case wire)
      return {
        id: local.id,
        userId: userId, // Not stored in cache, provided by caller
        activeSyncProvider: local.activeSyncProvider,
        syncEnabled: local.syncEnabled,
        syncInterval: local.syncInterval,
        syncOnSessionClose: local.syncOnSessionClose,
        syncOnLogin: local.syncOnLogin,
        conflictResolution: local.conflictResolution,
        version: 0, // Offline mode, version doesn't matter
        createdAt: local.createdAt,
        updatedAt: local.updatedAt,
      };
    } catch (error) {
      console.error('Failed to read IndexedDB cache', error);
      return null;
    }
  }

  /**
   * Get default settings for offline first-time users
   *
   * Last fallback: User is offline and has never synced before.
   * Return sensible defaults that match server defaults.
   *
   * @param userId - User ID
   * @returns Default settings
   */
  private getDefaultSettings(userId: string): UserSyncSettings {
    return {
      id: crypto.randomUUID(), // Temporary ID for offline mode
      userId: userId,
      ...DEFAULT_USER_SYNC_SETTINGS,
      version: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  }

  /**
   * Get cached cloud connection status
   *
   * Used by DataSyncSettings polling loop to keep GoogleDriveProviderCard UI in sync
   * across tabs when connection events occur.
   *
   * @returns Cached connection status or null if not yet populated
   */
  getCachedConnectionStatus(): ConnectionStatus | null {
    return this.cachedConnectionStatus;
  }

  /**
   * Update cached cloud connection status
   *
   * Called by handleConnectionEvent() after receiving realtime connection events
   * to refresh the cache with latest server state.
   *
   * Uses merge strategy to preserve known-good fields when new data is partial.
   * This prevents cache regression (e.g., losing folderId when reconnect event doesn't include it).
   *
   * @param status - New connection status
   */
  updateCachedConnectionStatus(status: ConnectionStatus): void {
    console.log('💾 [Service] Updating cachedConnectionStatus to:', status);

    // Merge instead of replace - preserve existing non-null values
    // This prevents flicker when partial updates arrive (e.g., reconnect without folderId)
    this.cachedConnectionStatus = {
      isConnected: status.isConnected, // Always take latest connection state
      email: status.email ?? this.cachedConnectionStatus?.email ?? null,
      folderId: status.folderId ?? this.cachedConnectionStatus?.folderId ?? null
    };

    console.log('💾 [Service] Cache updated (merged). Current cache value:', this.cachedConnectionStatus);
  }
}

/**
 * Singleton instance of UserSyncSettingsService
 *
 * IMPORTANT: Use this shared instance instead of creating new instances with `new UserSyncSettingsService()`.
 * This ensures the in-memory cache (cachedConnectionStatus) is shared across all components.
 */
export const userSyncSettingsService = new UserSyncSettingsService();
